(() => {
  const pendingForms = new WeakSet();
  const labelTimers = new WeakMap();
  let countRequest = 0;

  const getButtons = (form) => [...document.querySelectorAll('[data-sab-product-action]')].filter((button) => button.form === form);
  const isAvailable = (form) => {
    const field = form?.querySelector('[name="id"]');
    const selected = field?.selectedOptions?.[0] || field;
    return Boolean(field?.value) && selected?.dataset.available !== 'false' && !selected?.disabled;
  };
  const showError = (button, message) => {
    const status = document.getElementById(button.getAttribute('aria-describedby'));
    if (!status) return;
    status.textContent = message;
    status.hidden = !message;
  };
  const releaseForm = (form, resetLabels = false) => {
    pendingForms.delete(form);
    delete form.dataset.purchaseBusy;
    const available = isAvailable(form);
    getButtons(form).forEach((button) => {
      button.dataset.loading = 'false';
      button.disabled = !available;
      button.removeAttribute('aria-busy');
      button.classList.remove('is-checkout-loading');
      const label = button.querySelector('span');
      if (label && (resetLabels || !available)) label.textContent = available ? button.dataset.defaultLabel : button.dataset.soldOutLabel;
    });
  };
  const refreshCount = async (form) => {
    const request = ++countRequest;
    try {
      const response = await fetch(`${form.dataset.cartUrl}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) return;
      const cart = await response.json();
      if (request !== countRequest || !Number.isInteger(cart.item_count)) return;
      document.querySelectorAll('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count; });
    } catch (_) {}
  };
  const addItem = async (form, button) => {
    if (!button || button.disabled || (form && pendingForms.has(form))) return;
    const label = button.querySelector('span');
    if (!form?.matches('form[data-sticky-cart-form]') || !isAvailable(form)) {
      showError(button, button.dataset.errorMessage);
      return;
    }
    getButtons(form).forEach((control) => showError(control, ''));
    if (!form.reportValidity()) return;
    const data = new FormData(form);
    const quantity = Number(data.get('quantity') || 1);
    if (!Number.isSafeInteger(quantity) || quantity < 1) {
      showError(button, button.dataset.quantityError);
      return;
    }
    data.set('quantity', String(quantity));
    data.delete('return_to');
    data.delete('checkout');

    pendingForms.add(form);
    form.dataset.purchaseBusy = 'true';
    getButtons(form).forEach((control) => {
      clearTimeout(labelTimers.get(control));
      control.dataset.loading = 'true';
      control.disabled = true;
      const controlLabel = control.querySelector('span');
      if (controlLabel) controlLabel.textContent = control.dataset.defaultLabel;
    });
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-checkout-loading');
    if (label) label.textContent = button.dataset.loadingLabel;
    let added = false;
    let headingToCheckout = false;
    try {
      const response = await fetch(`${form.dataset.cartAddUrl}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: data
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(typeof result.description === 'string' ? result.description : button.dataset.errorMessage);
      }
      if (!result.id && !result.items?.length) throw new Error(button.dataset.errorMessage);
      if (button.dataset.sabProductAction === 'checkout') {
        window.location.assign(form.dataset.checkoutUrl || '/checkout');
        headingToCheckout = true;
        return;
      }
      added = true;
      if (label) label.textContent = button.dataset.addedLabel;
      void refreshCount(form);
    } catch (error) {
      if (label) label.textContent = button.dataset.errorLabel;
      showError(button, error instanceof TypeError || error instanceof SyntaxError ? button.dataset.errorMessage : error.message);
    } finally {
      if (!headingToCheckout) releaseForm(form);
      if (added) labelTimers.set(button, setTimeout(() => {
        if (label && !pendingForms.has(form)) label.textContent = isAvailable(form) ? button.dataset.defaultLabel : button.dataset.soldOutLabel;
      }, 1800));
    }
  };

  window.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-sab-product-action]') : null;
    if (!button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void addItem(button.form, button);
  }, true);

  window.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-sticky-cart-form]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const button = event.submitter?.matches('[data-sab-product-action]') ? event.submitter : getButtons(form).find((control) => control.dataset.sabProductAction === 'cart');
    void addItem(form, button);
  }, true);

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('form[data-sticky-cart-form]').forEach((form) => releaseForm(form, true));
  });
})();

(() => {
  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];

  const header = qs('[data-header]');
  const progress = qs('.page-progress span');
  const onScroll = () => {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    if (header) header.classList.toggle('is-scrolled', window.scrollY > 18);
    if (progress) progress.style.width = `${max > 0 ? Math.min(100, window.scrollY / max * 100) : 0}%`;
  };
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: .12, rootMargin: '0px 0px -40px' });
  qsa('[data-reveal]').forEach((element) => observer.observe(element));

  const searchOverlay = qs('[data-search-overlay]');
  const mobileMenu = qs('[data-mobile-menu]');
  const setOpen = (element, open) => {
    if (!element) return;
    element.hidden = !open;
    document.body.classList.toggle('menu-open', open);
    if (open) setTimeout(() => qs('input', element)?.focus(), 50);
  };
  qs('[data-search-open]')?.addEventListener('click', () => setOpen(searchOverlay, true));
  qs('[data-search-close]')?.addEventListener('click', () => setOpen(searchOverlay, false));
  qs('[data-menu-open]')?.addEventListener('click', () => setOpen(mobileMenu, true));
  qs('[data-menu-close]')?.addEventListener('click', () => setOpen(mobileMenu, false));
  searchOverlay?.addEventListener('click', (event) => { if (event.target === searchOverlay) setOpen(searchOverlay, false); });
  qsa('a', mobileMenu).forEach((link) => link.addEventListener('click', () => setOpen(mobileMenu, false)));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      setOpen(searchOverlay, false);
      setOpen(mobileMenu, false);
    }
  });

  const toast = qs('.product-toast');
  let toastTimer;
  const showToast = (title) => {
    if (!toast) return;
    const label = qs('small', toast);
    if (label) label.textContent = title;
    toast.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('show'), 4200);
  };
  const refreshCartCount = async () => {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    qsa('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count; });
  };

  qsa('form[data-product-form]:not([data-sticky-cart-form])').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = qs('[type="submit"]', form);
      if (!button || button.disabled || button.dataset.loading === 'true') return;
      button.dataset.loading = 'true';
      button.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        if (!response.ok) throw new Error('Unable to add item');
        const item = await response.json();
        await refreshCartCount();
        showToast(item.product_title || 'Product');
      } catch (error) {
        form.submit();
      } finally {
        button.dataset.loading = 'false';
        button.removeAttribute('aria-busy');
      }
    });
  });

  qsa('[data-product-section]').forEach((section) => {
    if (qs('[data-mobile-sticky-buy]', section)) document.body.classList.add('has-mobile-sticky-buy');
    const quantity = qs('input[name="quantity"]', section);
    qs('[data-qty-minus]', section)?.addEventListener('click', () => { quantity.value = Math.max(1, Number(quantity.value || 1) - 1); });
    qs('[data-qty-plus]', section)?.addEventListener('click', () => { quantity.value = Number(quantity.value || 1) + 1; });
    const variant = qs('select[name="id"]', section);
    variant?.addEventListener('change', () => {
      const selected = variant.options[variant.selectedIndex];
      const available = selected.dataset.available !== 'false';
      const price = qs('[data-product-price]', section);
      if (price && selected.dataset.price) price.textContent = selected.dataset.price;
      const stickyPrice = qs('[data-sticky-product-price]', section);
      if (stickyPrice && selected.dataset.price) stickyPrice.textContent = selected.dataset.price;
      const addPrice = qs('[data-add-product-price]', section);
      if (addPrice) addPrice.textContent = available ? selected.dataset.price : '';
      const busy = qs('form[data-sticky-cart-form]', section)?.dataset.purchaseBusy === 'true';
      qsa('[data-sab-product-action]', section).forEach((button) => {
        button.disabled = !available || busy;
        const label = qs('span', button);
        if (label && !busy) label.textContent = available ? button.dataset.defaultLabel : button.dataset.soldOutLabel;
        const status = document.getElementById(button.getAttribute('aria-describedby'));
        if (status) { status.textContent = ''; status.hidden = true; }
      });
    });
    qsa('[data-variant-pill]', section).forEach((pill) => pill.addEventListener('click', () => {
      if (!variant || pill.disabled) return;
      variant.value = pill.dataset.variantId;
      qsa('[data-variant-pill]', section).forEach((item) => item.classList.toggle('is-active', item === pill));
      variant.dispatchEvent(new Event('change', { bubbles: true }));
    }));

    const gallery = qs('[data-product-gallery]', section);
    const slides = qsa('[data-gallery-slide]', section);
    const dots = qsa('[data-gallery-dot]', section);
    const setActiveDot = (index) => {
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };
    let galleryFrame;
    gallery?.addEventListener('scroll', () => {
      cancelAnimationFrame(galleryFrame);
      galleryFrame = requestAnimationFrame(() => {
        const center = gallery.scrollLeft + gallery.clientWidth / 2;
        let activeIndex = 0;
        let closest = Infinity;
        slides.forEach((slide, index) => {
          const distance = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - center);
          if (distance < closest) { closest = distance; activeIndex = index; }
        });
        setActiveDot(activeIndex);
      });
    }, { passive: true });
    dots.forEach((dot, index) => dot.addEventListener('click', () => {
      const slide = slides[index];
      if (!gallery || !slide) return;
      gallery.scrollTo({ left: slide.offsetLeft - (gallery.clientWidth - slide.clientWidth) / 2, behavior: 'smooth' });
    }));

  });
})();
